import { memo } from 'react';
import {
  SUBTITLE_COLOR_OPTIONS,
  SUBTITLE_SIZE_OPTIONS,
  SUBTITLE_STROKE_OPTIONS,
  SUBTITLE_BACKGROUND_OPTIONS,
} from 'src/interfaces/enums';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { RadioGroup, Radio } from '../RadioButton';
import Button from '../Button';
import SubtitleTextPreview from './components/SubtitleTextPreview';
import styles from './styles.module.scss';

export interface ISubtitleStyleControlProps {
  color: SUBTITLE_COLOR_OPTIONS;
  size: SUBTITLE_SIZE_OPTIONS;
  stroke: SUBTITLE_STROKE_OPTIONS;
  background: SUBTITLE_BACKGROUND_OPTIONS;
  onColorChange: (color: SUBTITLE_COLOR_OPTIONS) => void;
  onSizeChange: (size: SUBTITLE_SIZE_OPTIONS) => void;
  onStrokeChange: (stroke: SUBTITLE_STROKE_OPTIONS) => void;
  onBackgroundChange: (background: SUBTITLE_BACKGROUND_OPTIONS) => void;
  onSetDefaultStyle: () => void;
}

const SubtitleStyleControl = ({
  color,
  size,
  stroke,
  background,
  onColorChange,
  onSizeChange,
  onStrokeChange,
  onBackgroundChange,
  onSetDefaultStyle,
}: ISubtitleStyleControlProps) => {
  const { t } = useTranslation('common');

  return (
    <div className={styles.container}>
      <SubtitleTextPreview
        color={color}
        size={size}
        stroke={stroke}
        background={background}
        className={styles.previewText}
      />

      <RadioGroup
        label={t('settings.subtitle.size')}
        name='size'
        value={size}
        onChange={(value) => onSizeChange(value as SUBTITLE_SIZE_OPTIONS)}
        className={styles.optionGroup}
      >
        {Object.values(SUBTITLE_SIZE_OPTIONS).map((sizeOption) => (
          <Radio key={sizeOption} value={sizeOption} aria-label={`size ${sizeOption}`}>
            <span className={classNames(styles.size, styles[`size-${sizeOption}`])}>A</span>
          </Radio>
        ))}
      </RadioGroup>

      <RadioGroup
        label={t('settings.subtitle.color')}
        name='color'
        value={color}
        onChange={(value) => onColorChange(value as SUBTITLE_COLOR_OPTIONS)}
        className={styles.optionGroup}
      >
        {Object.values(SUBTITLE_COLOR_OPTIONS).map((colorOption) => (
          <Radio key={colorOption} value={colorOption} aria-label={`color ${colorOption}`}>
            <div className={classNames(styles.color, styles[`color-${colorOption}`])}></div>
          </Radio>
        ))}
      </RadioGroup>

      {color !== SUBTITLE_COLOR_OPTIONS.black && (
        <RadioGroup
          label={t('settings.subtitle.stroke')}
          name='stroke'
          value={stroke}
          onChange={(value) => onStrokeChange(value as SUBTITLE_STROKE_OPTIONS)}
          className={styles.optionGroup}
        >
          {Object.values(SUBTITLE_STROKE_OPTIONS).map((strokeTypeOption) => (
            <Radio key={strokeTypeOption} value={strokeTypeOption} aria-label={`stroke ${strokeTypeOption}`}>
              <span className={classNames(styles.stroke, styles[`stroke-${strokeTypeOption}`])}>A</span>
            </Radio>
          ))}
        </RadioGroup>
      )}

      <RadioGroup
        label={t('settings.subtitle.background')}
        name='background'
        value={background}
        onChange={(value) => onBackgroundChange(value as SUBTITLE_BACKGROUND_OPTIONS)}
        className={styles.optionGroup}
      >
        {Object.values(SUBTITLE_BACKGROUND_OPTIONS).map((backgroundColorOption) => (
          <Radio
            key={backgroundColorOption}
            value={backgroundColorOption}
            aria-label={`background ${backgroundColorOption}`}
          >
            <div className={classNames(styles.background, styles[`background-${backgroundColorOption}`])}></div>
          </Radio>
        ))}
      </RadioGroup>

      <div className={styles.wrapperDefaultButton}>
        <Button onClick={onSetDefaultStyle} fullWidth>
          {t('settings.subtitle.default_style')}
        </Button>
      </div>
    </div>
  );
};

export default memo(SubtitleStyleControl);
